import { Content, type FilledLinkToWebField } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import cx from 'classnames';
import { Text } from 'components/@tedui';
import PrismicConditionalLink from 'components/PRISMIC/PrismicConditionalLink';
import { PrismicNextImage } from 'components/PRISMIC/PrismicNextImage';
import ContentContainer from 'components/content-container';
import mixpanel from 'lib/analytics/mixpanel';
import { useMemo, type JSX } from 'react';

/**
 * Props for `GridCard`.
 */
export type GridCardProps = SliceComponentProps<Content.GridCardSlice>;

/**
 * Component for "GridCard" Slices.
 */
const GridCard = ({ slice }: GridCardProps): JSX.Element => {
  const imgLinks = useMemo(() => {
    return slice.items.map(img => {
      return {
        ...img,
        imageLink: img.imageLink as FilledLinkToWebField,
        topicLink: img.topicLink as FilledLinkToWebField
      };
    });
  }, [slice.items]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-pureBlack pb-16 md-tui:pb-20"
    >
      <ContentContainer
        className="mx-auto max-w-7xl px-5 text-white sm-tui:px-10"
        noPadding
      >
        <div className={`mb-6 h-px w-full bg-white`} />
        <div className="flex flex-col gap-6 pb-6 sm-tui:flex-row sm-tui:items-end sm-tui:justify-between sm-tui:gap-6 sm-tui:pb-20">
          <Text
            variant="header1"
            color={{ override: 'white' }}
            useNewTextStyles
          >
            {slice.primary.title}
          </Text>

          <div className="whitespace-nowrap text-end text-tui-base font-semibold">
            <PrismicConditionalLink
              field={slice.primary.sideLink}
              onClick={() => {
                mixpanel.track('slice_click', {
                  current_url: window.location.href,
                  title: slice.primary.title.replace(' ', '-').toLowerCase(),
                  side_title: '',
                  item_number: '',
                  link_type: 'side'
                });
              }}
            >
              <Text
                variant="link1"
                color={{ override: 'white' }}
                UNSAFE_className="underline whitespace-nowrap"
                weight="font-semibold"
                useNewTextStyles
              >
                {slice.primary.sideText}
              </Text>
            </PrismicConditionalLink>
          </div>
        </div>
        <div className="flex flex-col items-start justify-center gap-10 sm-tui:-ml-5 sm-tui:grid-cols-3 sm-tui:flex-row sm-tui:gap-5 lg-tui:-ml-10 lg-tui:gap-10">
          {imgLinks.map((img, index) => (
            <div
              key={index}
              className={cx(
                'flex w-full flex-col !no-underline sm-tui:pl-5 lg-tui:pl-10',
                {
                  'sm-tui:border-l-thin sm-tui:border-[#717171]': index !== 0
                }
              )}
            >
              <PrismicConditionalLink
                field={img.imageLink}
                onClick={() => {
                  mixpanel.track('slice_click', {
                    current_url: window.location.href,
                    title: slice.primary.title.replace(' ', '-').toLowerCase(),
                    side_title: '',
                    item_number: index + 1,
                    link_type: 'image'
                  });
                }}
              >
                <PrismicNextImage
                  field={img.image}
                  style={{
                    margin: 'auto',
                    width: '100%',
                    position: 'relative'
                  }}
                />
              </PrismicConditionalLink>
              <PrismicConditionalLink
                field={img.topicLink}
                className="mb-1 mt-6"
                onClick={() => {
                  mixpanel.track('slice_click', {
                    current_url: window.location.href,
                    title: slice.primary.title.replace(' ', '-').toLowerCase(),
                    side_title: '',
                    item_number: index + 1,
                    link_type: 'topic'
                  });
                }}
              >
                <Text
                  variant={'link2'}
                  color={{ override: '#767676' }}
                  useNewTextStyles
                >
                  {img.topic}
                </Text>
              </PrismicConditionalLink>

              <PrismicConditionalLink
                field={img.imageLink}
                onClick={() => {
                  mixpanel.track('slice_click', {
                    current_url: window.location.href,
                    title: slice.primary.title.replace(' ', '-').toLowerCase(),
                    side_title: '',
                    item_number: index + 1,
                    link_type: 'title'
                  });
                }}
              >
                <Text
                  variant={'header4'}
                  color={{ override: 'white' }}
                  weight="font-semibold"
                  useNewTextStyles
                >
                  {img.talkTitle}
                </Text>
              </PrismicConditionalLink>
            </div>
          ))}
        </div>
      </ContentContainer>
    </section>
  );
};

export default GridCard;
